import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Code, Smartphone, Wifi, Server, Shield, Monitor, 
  Home, Info, Layers, Briefcase, PhoneCall, ChevronDown, GraduationCap, Sun 
} from 'lucide-react';
import logo from '../../assets/transparent SingularityTechLogo.png';
import whiteLogo from '../../assets/SingularityTech White Transparent Logo.png';
import './Navbar.css';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const location = useLocation();

  const links = [
    { name: 'Home', path: '/', icon: <Home size={24} /> },
    { name: 'About Us', path: '/about', icon: <Info size={24} /> },
    { name: 'Services', path: '/services', icon: <Layers size={24} /> },
    { name: 'Careers', path: '/careers', icon: <Briefcase size={24} /> },
    { name: 'Contact', path: '/contact', icon: <PhoneCall size={24} /> },
  ];

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (document.body.classList.contains('dark-mode')) {
      setIsDark(true);
    }

    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'class') {
          setIsDark(document.body.classList.contains('dark-mode'));
        }
      }
    });

    observer.observe(document.body, { attributes: true });
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const desktopLinksElements = [];
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    
    if (link.name === 'Services') {
      desktopLinksElements.push(
        <div key="services-dropdown" className="nav-item-dropdown">
          <Link 
            to="/services" 
            className={`nav-link ${(location.pathname === '/services' || location.pathname === '/solar-assessment') ? 'active' : ''}`}
          >
            Services <ChevronDown size={14} className="dropdown-arrow" />
          </Link>
          <div className="dropdown-menu">
            <Link to="/services" className="dropdown-link">All Services</Link>
            <Link to="/solar-assessment" className="dropdown-link">Solar Assessment</Link>
          </div>
        </div>
      );
    } else if (link.name === 'Careers') {
      desktopLinksElements.push(
        <div key="careers-dropdown" className="nav-item-dropdown">
          <Link 
            to="/careers" 
            className={`nav-link ${(location.pathname === '/careers' || location.pathname === '/training') ? 'active' : ''}`}
          >
            Careers <ChevronDown size={14} className="dropdown-arrow" />
          </Link>
          <div className="dropdown-menu">
            <Link to="/careers" className="dropdown-link">Job Openings</Link>
            <Link to="/training" className="dropdown-link">Training Programs</Link>
          </div>
        </div>
      );
    } else {
      desktopLinksElements.push(
        <Link 
          key={link.name} 
          to={link.path} 
          className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
        >
          {link.name}
        </Link>
      );
    }
  }

  const mobileLinksElements = [];
  for (let j = 0; j < links.length; j++) {
    const link = links[j];
    
    if (link.name === 'Services') {
      mobileLinksElements.push(
        <React.Fragment key="mobile-services-group">
          <div className="mobile-nav-divider">SERVICES</div>
          <Link to="/services" className={`mobile-nav-link ${location.pathname === '/services' ? 'active' : ''}`}>
            <span className="mobile-link-icon"><Layers size={24} /></span>
            All Services
          </Link>
          <Link to="/solar-assessment" className={`mobile-nav-link ${location.pathname === '/solar-assessment' ? 'active' : ''}`}>
            <span className="mobile-link-icon"><Sun size={24} /></span>
            Solar Assessment
          </Link>
        </React.Fragment>
      );
    } else if (link.name === 'Careers') {
      mobileLinksElements.push(
        <React.Fragment key="mobile-careers-group">
          <div className="mobile-nav-divider">CAREERS & OPPORTUNITIES</div>
          <Link to="/careers" className={`mobile-nav-link ${location.pathname === '/careers' ? 'active' : ''}`}>
            <span className="mobile-link-icon"><Briefcase size={24} /></span>
            Job Openings
          </Link>
          <Link to="/training" className={`mobile-nav-link ${location.pathname === '/training' ? 'active' : ''}`}>
            <span className="mobile-link-icon"><GraduationCap size={24} /></span>
            Training Programs
          </Link>
          <div className="mobile-nav-divider">SUPPORT</div>
        </React.Fragment>
      );
    } else if (link.name !== 'Contact') {
      mobileLinksElements.push(
        <Link key={link.name} to={link.path} className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}>
          <span className="mobile-link-icon">{link.icon}</span>
          {link.name}
        </Link>
      );
    } else {
      mobileLinksElements.push(
        <Link key={link.name} to={link.path} className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}>
          <span className="mobile-link-icon">{link.icon}</span>
          {link.name}
        </Link>
      );
    }
  }

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="nav-logo" onClick={() => setIsOpen(false)}>
            <img src={isDark ? whiteLogo : logo} alt="Singularity Tech Logo" className="nav-logo-img" />
          </Link>
          <div className="nav-links">
            {desktopLinksElements}
          </div>
          <div className={`hamburger ${isOpen ? 'open' : ''}`} onClick={toggleMenu}>
            <span></span><span></span><span></span>
          </div>
        </div>
      </nav>

      <div className={`mobile-overlay ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(false)}></div>

      <div className={`mobile-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-icons-bg">
          <Code size={100} className="drawer-icon d-icon-1" />
          <Server size={80} className="drawer-icon d-icon-2" />
          <Wifi size={120} className="drawer-icon d-icon-3" />
          <Shield size={90} className="drawer-icon d-icon-4" />
          <Smartphone size={70} className="drawer-icon d-icon-5" />
          <Monitor size={110} className="drawer-icon d-icon-6" />
        </div>
        <div className="mobile-nav-links">
          {mobileLinksElements}
        </div>
      </div>
    </>
  );
}